<template>
  <div id="docu-managers-table-container">
    <div id="title-container">
      <h2>{{ t('adminPanel.docuManager') }}</h2>
      <div id="docu-managers-counter">{{ docuManagers?.length ?? 0 }}</div>
    </div>
    <table-wrapper
      v-model:filters="filters"
      v-model:page-options="pageOptions"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="docuManagers"
      :loading="isLoading"
      :reset-selection-on-page-or-filter-change="false"
      :mode="TableMode.CLIENT_SIDE"
      :selection="mode === DocuManagersTableMode.NEW_PROJECT"
      @update:selected="handleSelectionChange"
    >
    </table-wrapper>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import axios from 'axios';
  import endpointsList from '../../helpers/endpointsList';
  import { AdGroup, IDocuManager } from 'types';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '@/components/TableWrapper';
  import { DocuManagersTableMode } from './DocuManagersTable.types';

  const { t } = useI18n();
  const { createErrorToast } = useToast();

  defineProps<{
    mode?: DocuManagersTableMode;
  }>();
  const emit = defineEmits<{
    (e: 'update:selected-docu-managers', selected: IDocuManager[]): void;
  }>();

  const docuManagers = ref<IDocuManager[]>([]);
  const { pageOptions, filters, isLoading } = useTableWrapper<IDocuManager>(
    TableMode.CLIENT_SIDE,
    'lastName',
    getDocuManagers,
    undefined,
    false,
  );
  const filterConfiguration = new TableFiltersBuilder<IDocuManager>({
    translationKey: 'generic',
  })
    .addInputFilter('firstName', {
      width: '208px',
    })
    .addInputFilter('lastName', {
      width: '208px',
    })
    .addInputFilter('email', {
      width: '208px',
    })
    .build();
  const tableFields = computed(() =>
    new TableFieldsBuilder<IDocuManager>({
      sortable: true,
      labelTranslationKey: 'generic',
    })
      .addField('firstName', {
        width: '36%',
      })
      .addField('lastName', {
        width: '28%',
      })
      .addField('email', {
        width: '34%',
      })
      .build(),
  );

  async function getDocuManagers(): Promise<void> {
    try {
      const response = await axios.get(
        endpointsList.adIntegration.getAllUsersFromSupplierProjectAdGroup(
          AdGroup.DOCU_MANAGER,
        ),
      );
      docuManagers.value = response.data;
    } catch {
      createErrorToast(t('docuManagersTable.errors.couldNotGetDocuManagers'));
    }
  }

  function handleSelectionChange(selectedDocuManagers: IDocuManager[]): void {
    emit('update:selected-docu-managers', selectedDocuManagers);
  }
</script>

<style scoped lang="scss">
  #docu-managers-table-container {
    border-top: 1px solid $border-color;
    padding-top: $tab-view-margin-top;

    #title-container {
      display: flex;
      align-items: center;
      column-gap: 10px;
      margin-bottom: 20px;

      h2 {
        @include subheading;
      }

      #docu-managers-counter {
        align-self: flex-start;
        @include heading-counter;
      }
    }
  }
</style>
